import { useMemo, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";
import ModalForm from "../modal-form";
import { Campo } from "../../services/tipos";
import { TipoFormulario } from "../../services/constantes";
import {
    TipoCalculoComissao,
    TipoMovimentacaoComissao,
    TipoPeriodicidadePagamento,
    MomentoApuracaoComissao,
    UsuarioComissaoRegraDto
} from "../../services/comissao";

interface CategoriaSimples {
    id: string;
    nome: string;
}

export interface VeiculoSimples {
    id: string;
    placa: string;
    modelo?: string;
}

interface TabelaComissaoProps {
    value: UsuarioComissaoRegraDto[];
    onChange: (regras: UsuarioComissaoRegraDto[]) => void;
    categorias: CategoriaSimples[];
    /** Frota da empresa, para as regras de Locação por veículo. */
    veiculos?: VeiculoSimples[];
    apenasLeitura: boolean;
    /** Formulário aberto só para visualização: trava a tabela sem o aviso de "própria comissão". */
    modoVisualizacao?: boolean;
    idUsuario: string;
    idEmpresa: string;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

const novoRascunho = (
    idUsuario: string,
    idEmpresa: string
) => ({
    id: EMPTY_GUID,
    idUsuario,
    idEmpresa,
    tipoMovimentacao: 1 as TipoMovimentacaoComissao,
    tipoCalculo: 1 as TipoCalculoComissao,
    valor: 0,
    valorPercentual: 0,
    valorFixo: 0,
    teto: null,
    idCategoria: null,
    periodicidade: null,
    idVeiculo: null,
    // Padrão do marco: o início do contrato é o que o locador associa ao negócio fechado.
    momentoApuracao: 1 as MomentoApuracaoComissao
});

const gerarId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

const labelTipo = (t: TipoMovimentacaoComissao) =>
    t === 1 ? "Pagamentos" : t === 3 ? "Locações" : "Lançamentos";

const labelMomento = (m?: MomentoApuracaoComissao | null) => {
    switch (m) {
        case 2: return "No cadastro";
        case 3: return "No 1º pagamento";
        case 4: return "No encerramento";
        case 1: return "No início";
        default: return "-";
    }
};

/** Placa da regra, ou o curinga quando a regra vale para a frota inteira. */
const labelVeiculo = (regra: UsuarioComissaoRegraDto, veiculos: VeiculoSimples[]) => {
    if (regra.tipoMovimentacao !== 3) return "-";
    if (!regra.idVeiculo) return "Todos";
    return regra.placaVeiculo
        || veiculos.find(v => v.id === regra.idVeiculo)?.placa
        || "Veículo";
};

const labelPeriodicidade = (p?: TipoPeriodicidadePagamento | null) => {
    switch (p) {
        case 4: return "Diária";
        case 1: return "Semanal";
        case 2: return "Quinzenal";
        case 3: return "Mensal";
        default: return "Todas";
    }
};

const labelCalc = (c: TipoCalculoComissao) =>
    c === 1 ? "Percentual" : "Fixo";

const formatarValor = (regra: UsuarioComissaoRegraDto) =>
    regra.tipoCalculo === 1
        ? `${regra.valor}%`
        : `R$ ${regra.valor.toFixed(2)}`;

const formatarTeto = (regra: UsuarioComissaoRegraDto) =>
    regra.teto != null ? `R$ ${regra.teto.toFixed(2)}` : "-";

const apiConfigStub = {
    endpoint: "",
    requisicaoBemSucedida: () => { },
    tratarErro: () => { },
    idTenentCampo: ""
};

export const TabelaComissao = ({
    value,
    onChange,
    categorias,
    veiculos = [],
    apenasLeitura,
    modoVisualizacao = false,
    idUsuario,
    idEmpresa
}: TabelaComissaoProps) => {
    const [exibirModalRegra, setExibirModalRegra] = useState(false);
    const [regraEditando, setRegraEditando] =
        useState<(UsuarioComissaoRegraDto & Record<string, any>) | null>(null);
    const [categoriasEscolhidas, setCategoriasEscolhidas] = useState<CategoriaSimples[]>([]);
    // Vazio = a regra vale para todos os veículos. É o curinga, igual ao "Todas" da periodicidade.
    const [veiculosEscolhidos, setVeiculosEscolhidos] = useState<VeiculoSimples[]>([]);

    const handleAdicionar = () => {
        setCategoriasEscolhidas([]);
        setVeiculosEscolhidos([]);
        setRegraEditando(novoRascunho(idUsuario, idEmpresa));
        setExibirModalRegra(true);
    };

    const handleEditar = (regra: UsuarioComissaoRegraDto) => {
        const catSel = regra.idCategoria
            ? categorias.filter(c => c.id === regra.idCategoria)
            : [];
        setCategoriasEscolhidas(catSel);
        setVeiculosEscolhidos(regra.idVeiculo ? veiculos.filter(v => v.id === regra.idVeiculo) : []);
        setRegraEditando({
            ...regra,
            valorPercentual: regra.tipoCalculo === 1 ? regra.valor : 0,
            valorFixo: regra.tipoCalculo === 2 ? regra.valor : 0,
        });
        setExibirModalRegra(true);
    };

    const handleRemover = (id: string) => {
        onChange(value.filter(r => r.id !== id));
    };

    const fecharModal = () => {
        setExibirModalRegra(false);
        setRegraEditando(null);
    };

    const handleSave = (entidade: any) => {
        const tipoMov = Number(entidade.tipoMovimentacao) as TipoMovimentacaoComissao;
        const tipoCalc = Number(entidade.tipoCalculo) as TipoCalculoComissao;
        const valor = tipoCalc === 1
            ? (Number(entidade.valorPercentual) || 0)
            : (Number(entidade.valorFixo) || 0);
        const teto =
            tipoCalc === 1 && entidade.teto != null && entidade.teto !== ""
                ? Number(entidade.teto)
                : null;

        if (tipoMov === 2 && categoriasEscolhidas.length === 0) {
            toast.error("Selecione ao menos uma categoria para regras de Lançamentos.");
            return;
        }
        if (valor <= 0) {
            toast.error("Informe um valor maior que zero.");
            return;
        }
        if (tipoCalc === 1 && valor > 100) {
            toast.error("Percentual não pode ser maior que 100.");
            return;
        }

        const ehNova = (entidade.id || EMPTY_GUID) === EMPTY_GUID;

        if (tipoMov === 3) {
            // Locações: uma comissão por contrato fechado. Nenhum veículo escolhido = regra curinga,
            // que vale para a frota inteira e perde para qualquer regra de veículo específico.
            const momento = entidade.momentoApuracao != null && entidade.momentoApuracao !== ""
                ? Number(entidade.momentoApuracao) as MomentoApuracaoComissao
                : null;
            if (momento == null) {
                toast.error("Escolha quando a comissão da locação deve ser apurada.");
                return;
            }

            const proximo = [...value];
            if (!ehNova) {
                const idx = proximo.findIndex(r => r.id === entidade.id);
                if (idx >= 0) proximo.splice(idx, 1);
            }

            const alvos: (VeiculoSimples | null)[] =
                veiculosEscolhidos.length > 0 ? veiculosEscolhidos : [null];

            const duplicadas: string[] = [];
            for (const veiculo of alvos) {
                const jaExiste = proximo.some(r =>
                    r.tipoMovimentacao === 3 && (r.idVeiculo ?? null) === (veiculo?.id ?? null));
                if (jaExiste) {
                    duplicadas.push(veiculo ? veiculo.placa : "Todos os veículos");
                    continue;
                }
                proximo.push({
                    id: gerarId(),
                    idUsuario, idEmpresa,
                    tipoMovimentacao: tipoMov, tipoCalculo: tipoCalc,
                    valor, teto,
                    idCategoria: null, nomeCategoria: null,
                    idVeiculo: veiculo?.id ?? null,
                    placaVeiculo: veiculo?.placa ?? null,
                    momentoApuracao: momento
                });
            }

            if (duplicadas.length > 0) {
                toast.warn(`Já existem regras de Locação para: ${duplicadas.join(", ")}. Essas foram ignoradas.`);
            }

            onChange(proximo);
        } else if (tipoMov === 1) {
            // Pagamentos: uma regra por periodicidade
            const periodicidade = entidade.periodicidade != null && entidade.periodicidade !== ""
                ? Number(entidade.periodicidade) as TipoPeriodicidadePagamento
                : null;

            const normalizePeriodicidade = (p: any) => (p === 0 || p == null) ? null : p;
            const duplicada = value.some(r => {
                if (!ehNova && r.id === entidade.id) return false;
                return r.tipoMovimentacao === 1 && normalizePeriodicidade(r.periodicidade) === normalizePeriodicidade(periodicidade);
            });
            if (duplicada) {
                const label = periodicidade != null ? labelPeriodicidade(periodicidade) : "genérica (Todas)";
                toast.error(`Já existe uma regra de Pagamentos para periodicidade ${label}. Edite ou remova a regra existente.`);
                return;
            }

            const regra: UsuarioComissaoRegraDto = {
                id: entidade.id || EMPTY_GUID,
                idUsuario, idEmpresa,
                tipoMovimentacao: tipoMov, tipoCalculo: tipoCalc,
                valor, teto,
                periodicidade,
                idCategoria: null, nomeCategoria: null,
                idVeiculo: null, momentoApuracao: null
            };

            const proximo = [...value];
            const idx = ehNova ? -1 : proximo.findIndex(r => r.id === regra.id);
            if (idx >= 0) {
                proximo[idx] = regra;
            } else {
                proximo.push({ ...regra, id: gerarId() });
            }
            onChange(proximo);
        } else {
            // Lançamentos: uma regra por categoria selecionada
            const proximo = [...value];

            // Se editando, remove a regra antiga antes de inserir as novas
            if (!ehNova) {
                const idx = proximo.findIndex(r => r.id === entidade.id);
                if (idx >= 0) proximo.splice(idx, 1);
            }

            const duplicadas: string[] = [];
            for (const cat of categoriasEscolhidas) {
                const jaExiste = proximo.some(r =>
                    r.tipoMovimentacao === 2 && r.idCategoria === cat.id
                );
                if (jaExiste) {
                    duplicadas.push(cat.nome);
                    continue;
                }
                proximo.push({
                    id: gerarId(),
                    idUsuario, idEmpresa,
                    tipoMovimentacao: tipoMov, tipoCalculo: tipoCalc,
                    valor, teto,
                    idCategoria: cat.id,
                    nomeCategoria: cat.nome,
                    idVeiculo: null, momentoApuracao: null
                });
            }

            if (duplicadas.length > 0) {
                toast.warn(`Já existem regras para: ${duplicadas.join(", ")}. Essas foram ignoradas.`);
            }

            onChange(proximo);
        }

        fecharModal();
    };

    const camposModal: Campo[] = useMemo(() => [
        {
            linha: 1, coluna: 1, largura: 12,
            nome: "tipoMovimentacao", label: "Movimentação",
            tipo: "select", required: true, visible: true,
            opcoes: [
                { label: "Pagamentos", valor: 1 },
                { label: "Lançamentos", valor: 2 },
                { label: "Locações", valor: 3 }
            ],
            ajudinha: {
                texto: "Pagamentos comissiona cada parcela do aluguel. Locações paga uma comissão única por contrato fechado. Lançamentos comissiona receitas avulsas por categoria.",
                autoCloseTime: 8
            }
        },
        {
            linha: 2, coluna: 1, largura: 12,
            nome: "periodicidade", label: "Periodicidade",
            tipo: "select", required: false,
            visible: (v: any) => Number(v?.tipoMovimentacao) === 1,
            opcoes: [
                { label: "Todas (regra genérica)", valor: "" },
                { label: "Diária", valor: 4 },
                { label: "Semanal", valor: 1 },
                { label: "Quinzenal", valor: 2 },
                { label: "Mensal", valor: 3 },
            ]
        },
        {
            linha: 3, coluna: 1, largura: 12,
            nome: "categorias", label: "Categoria(s)",
            tipo: "multiselect", required: false,
            visible: (v: any) => Number(v?.tipoMovimentacao) === 2,
            selectMultiProps: {
                dataSource: categorias,
                onSelectionChange: (data: any) => setCategoriasEscolhidas(data),
                modo: regraEditando?.id === EMPTY_GUID ? TipoFormulario.tpCadastro : TipoFormulario.tpEdicao,
                initialSelected: categoriasEscolhidas,
                criticarRepeticao: true,
                optionLabelField: "nome",
            }
        },
        {
            linha: 3, coluna: 1, largura: 12,
            nome: "veiculos", label: "Veículo(s)",
            tipo: "multiselect", required: false,
            visible: (v: any) => Number(v?.tipoMovimentacao) === 3,
            selectMultiProps: {
                dataSource: veiculos,
                onSelectionChange: (data: any) => setVeiculosEscolhidos(data),
                modo: regraEditando?.id === EMPTY_GUID ? TipoFormulario.tpCadastro : TipoFormulario.tpEdicao,
                initialSelected: veiculosEscolhidos,
                criticarRepeticao: true,
                optionLabelField: "placa;modelo",
                orderByField: "placa",
                // Campo vazio já diz o que significa: sem veículo escolhido, a regra vale para a
                // frota inteira. Um "Selecione as opções" faria parecer que a escolha é obrigatória.
                placeholder: "Todos os veículos",
            },
            ajudinha: {
                texto: "Escolha um ou mais veículos para uma comissão diferenciada. A regra sem veículo vale para o resto da frota, e qualquer regra de veículo específico vence sobre ela.",
                autoCloseTime: 8
            }
        },
        {
            linha: 3, coluna: 2, largura: 12,
            nome: "momentoApuracao", label: "Quando a comissão é ganha",
            tipo: "select", required: true,
            visible: (v: any) => Number(v?.tipoMovimentacao) === 3,
            opcoes: [
                { label: "No início da locação", valor: 1 },
                { label: "No cadastro da locação", valor: 2 },
                { label: "No primeiro pagamento", valor: 3 },
                { label: "No encerramento da locação", valor: 4 }
            ],
            ajudinha: {
                texto: "Define em que mês a comissão aparece no relatório. Enquanto o marco não acontece, a comissão não é contada — uma regra 'no encerramento' só paga quando o contrato termina.",
                autoCloseTime: 8
            }
        },
        {
            linha: 4, coluna: 1, largura: 6,
            nome: "tipoCalculo", label: "Tipo de cálculo",
            tipo: "select", required: true, visible: true,
            opcoes: [
                { label: "Percentual", valor: 1 },
                { label: "Fixo", valor: 2 }
            ]
        },
        {
            linha: 4, coluna: 2, largura: 6,
            nome: "valorPercentual", label: "Valor",
            tipo: "percentual", required: true,
            visible: (v: any) => Number(v?.tipoCalculo) === 1
        },
        {
            linha: 4, coluna: 2, largura: 6,
            nome: "valorFixo", label: "Valor",
            tipo: "money", required: true,
            visible: (v: any) => Number(v?.tipoCalculo) === 2
        },
        {
            linha: 5, coluna: 1, largura: 6,
            nome: "teto", label: "Teto (R$) — opcional",
            tipo: "money", required: false,
            visible: (v: any) => Number(v?.tipoCalculo) === 1
        }
    ], [categorias, categoriasEscolhidas, regraEditando, veiculos, veiculosEscolhidos]);

    const pages = useMemo(() => [
        { titulo: "Regra de comissão", campos: [...new Set(camposModal.map(c => c.nome))] }
    ], [camposModal]);

    return (
        <div>
            {apenasLeitura && !modoVisualizacao && (
                <div
                    style={{
                        background: "#FFF3CD",
                        border: "1px solid #FFEEBA",
                        padding: 8,
                        marginBottom: 12,
                        borderRadius: 4
                    }}
                >
                    Você não pode editar a própria comissão. Peça a um administrador.
                </div>
            )}

            <Table striped bordered hover size="sm" style={{ marginBottom: 12 }}>
                <thead>
                    <tr>
                        <th>Movimentação</th>
                        <th>Periodicidade</th>
                        <th>Categoria</th>
                        <th>Veículo</th>
                        <th>Apuração</th>
                        <th>Tipo</th>
                        <th>Valor</th>
                        <th>Teto</th>
                        {!apenasLeitura && <th style={{ width: 80 }}>Ações</th>}
                    </tr>
                </thead>
                <tbody>
                    {value.length === 0 ? (
                        <tr>
                            <td
                                colSpan={apenasLeitura ? 8 : 9}
                                style={{ textAlign: "center", padding: 12 }}
                            >
                                Nenhuma regra cadastrada.
                            </td>
                        </tr>
                    ) : (
                        value.map(r => (
                            <tr key={r.id}>
                                <td>{labelTipo(r.tipoMovimentacao)}</td>
                                <td>{r.tipoMovimentacao === 1 ? labelPeriodicidade(r.periodicidade) : "-"}</td>
                                <td>{r.nomeCategoria ?? "-"}</td>
                                <td>{labelVeiculo(r, veiculos)}</td>
                                <td>{r.tipoMovimentacao === 3 ? labelMomento(r.momentoApuracao) : "-"}</td>
                                <td>{labelCalc(r.tipoCalculo)}</td>
                                <td>{formatarValor(r)}</td>
                                <td>{formatarTeto(r)}</td>
                                {!apenasLeitura && (
                                    <td>
                                        <Button
                                            size="sm"
                                            variant="link"
                                            onClick={() => handleEditar(r)}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="link"
                                            onClick={() => handleRemover(r.id)}
                                            title="Remover regra"
                                        >
                                            <FaTrash />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {!apenasLeitura && (
                <Button onClick={handleAdicionar} variant="primary">
                    + Adicionar regra
                </Button>
            )}

            {exibirModalRegra && regraEditando && (
                <ModalForm
                    show={exibirModalRegra}
                    onHide={fecharModal}
                    onCancel={fecharModal}
                    carregando={false}
                    titulo="Regra de comissão"
                    tamanho="lg"
                    modo={
                        regraEditando.id === EMPTY_GUID
                            ? TipoFormulario.tpCadastro
                            : TipoFormulario.tpEdicao
                    }
                    entidadeSelecionada={regraEditando}
                    pages={pages}
                    campos={camposModal}
                    apiConfig={apiConfigStub as any}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default TabelaComissao;
